import {
  getRelativePosition,
  useGetBibliography,
  useUpdateBibliography,
} from "@/api/bibliography/bibliography";
import AddFileModal from "@/components/modal/AddFileModal";
import { Separator } from "@/components/ui/separator";
import { Bibliography } from "@/lib/types";
import { useParams, useSearchParams } from "react-router-dom";

import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  appendNoDuplicateBibliographyNumbersRefine,
  bibliographySchema,
} from "@/api/bibliography/bibliography-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { TwoDimensionalInput } from "@/components/form-items/DimensionsInput";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { computeFieldLongSummaryBibliographie } from "@/lib/computeFields";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const schema = appendNoDuplicateBibliographyNumbersRefine(
  bibliographySchema.passthrough(),
);

const EditBibliography = ({ bibliography }: { bibliography: Bibliography }) => {
  const { instanceData } = useAuth();
  const form = useForm({
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: bibliography,
  });

  const { data: attachments } = useGetAttachmentsByDocId(bibliography.id);
  const [, setSearchParam] = useSearchParams();

  const { t } = useTranslation();

  const biblioUpdater = useUpdateBibliography();

  const handleResume = (data: any) => {
    return computeFieldLongSummaryBibliographie(data, {
      language: getMainLanguage(),
    });
  };

  const { relativePosition } = useDashboardParams<Bibliography>({
    statusField: "bibliographiePublicationStatus",
    resourceName: "bibliography",
    relativePosition: {
      currentId: bibliography.id,
      positionFetcher: getRelativePosition,
    },
  });

  const isModulePhotoEnabled = useMemo(() => {
    return instanceData?.isModulePhotoEnabled;
  }, [instanceData?.isModulePhotoEnabled]);

  const queryClient = useQueryClient();

  return (
    <FormLayout
      id={bibliography.id}
      title={bibliography.bibliographieDescriptionTitre}
      reference={bibliography.bibliographieDescriptionNumeroBibliographie}
      publicationStatus={bibliography.bibliographiePublicationStatus}
      type="bibliography"
      relativePosition={relativePosition}
      picturesData={
        attachments && { pictures: attachments, type: "bibliography" }
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["bibliographies"],
        });
        queryClient.invalidateQueries({
          queryKey: ["bibliography", bibliography.id],
        });

        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: bibliography.id }],
        });
      }}
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            biblioUpdater.mutate(
              {
                ...values,
                ...(status && { bibliographiePublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("bibliographySavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (
              invalid.bibliographieDescriptionNumeroBibliographie != undefined
            )
              setSearchParam({ tab: "description" });
          },
        )()
      }
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        tabs={[
          {
            value: "description",
            label: t("bibliographyDescription"),
          },
          {
            value: "sources",
            label: t("sources"),
          },
          {
            value: "files",
            label: t("fichiers"),
          },
          {
            value: "links",
            label: t("linkedEntries"),
          },
        ]}
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Text name="bibliographieDescriptionNumeroBibliographie" />
            <AF.Text name="bibliographieDescriptionAuteur" />
            <AF.Text className="w-16" name="bibliographieDescriptionAnnee" />
            <AF.Text name="bibliographieDescriptionTitre" />
            <AF.Text name="bibliographieDescriptionTitreDOrigine" />
            <AF.Text name="bibliographieDescriptionSousTitre" />
            <AF.Row>
              <ModifiableList
                name="bibliographieDescriptionType"
                listKey="liste-types-biblio"
              />
              <AF.Checkbox
                align={true}
                name="bibliographieDescriptionIllustrations"
              />
            </AF.Row>

            <AF.Text name="bibliographieDescriptionCollection" />
            <AF.Text name="bibliographieDescriptionEditeur" />
            <AF.Row>
              <ModifiableList
                name="bibliographieDescriptionVille"
                listKey="liste-villes"
              />
              <ModifiableList
                name="bibliographieDescriptionPays"
                listKey="liste-pays"
              />
            </AF.Row>
            <AF.Text name="bibliographieDescriptionNomRevue" />
            <AF.Row>
              <AF.Text name="bibliographieDescriptionNumeroRevue" />
              <AF.Text name="bibliographieDescriptionDatesPublication" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="bibliographieDescriptionPageArticle" />
              <AF.Text name="bibliographieDescriptionNombrePages" />
            </AF.Row>
            <ModifiableList
              name="bibliographieDescriptionContexture"
              listKey="liste-description-biblio"
            />
            <ModifiableList
              name="bibliographieDescriptionCouverture"
              listKey="liste-description-biblio"
            />
            <AF.Row>
              <AF.Text name="bibliographieDescriptionIsbn" />
              <AF.Text name="bibliographieDescriptionAutreCodeBibliographie" />
            </AF.Row>
          </AF.Col>
          <AF.Col>
            <AF.Row>
              <AF.Checkbox name="bibliographieValidationReprosOeuvresOk" />
              <AF.Checkbox name="bibliographieValidationReprosPhotosOk" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="bibliographieValidationReprosDocumentsOk" />
              <AF.Checkbox name="bibliographieValidationListeDesOeuvresOk" />
            </AF.Row>
            <Separator />
            <AF.Row>
              <AF.Checkbox name="bibliographieValidationPublicationNumerisee" />
              <AF.Checkbox name="bibliographieValidationPdfEtOcrOk" />
            </AF.Row>
            <AF.Checkbox name="bibliographieValidationNoticeBibliographiqueOk" />
            <AF.RichText name="bibliographieDescriptionResumeCourt" />
            <AF.RichText
              name="bibliographieDescriptionResumeLong"
              computeAction={handleResume}
            />
            <AF.RichText name="bibliographieDescriptionNotes" />
            <ModifiableList
              name="bibliographieDescriptionLocalisationReference"
              listKey="liste-bibliotheques-archives"
            />
            <AF.Row>
              <AF.Text name="bibliographieDescriptionRangementReference1" />
              <AF.Text name="bibliographieDescriptionRangementReference2" />
            </AF.Row>
            <TwoDimensionalInput parentName="bibliographieDescriptionDimensions" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={<AddFileModal id={bibliography.id} />}
        >
          <AF.OthersFilesTable docId={bibliography.id} name="diversfiles" />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={bibliography.id}
            type="bibliography"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "artworkInBibliography",
                      label: t("entryartworks"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                          label: t(
                            "bibliographieDescriptionNumeroBibliographie",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "bibliography.bibliographieDescriptionResumeLong",
                          label: t("bibliographieDescriptionResumeLong"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionReproduitPage",
                          label: t(
                            "oeuvresDansBibliographieDescriptionReproduitPage",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionCitePage",
                          label: t(
                            "oeuvresDansBibliographieDescriptionCitePage",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionPhotographe",
                          label: t(
                            "oeuvresDansBibliographieDescriptionPhotographe",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionNonPagine",
                          label: t(
                            "oeuvresDansBibliographieDescriptionNonPagine",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionReproductionCouleur",
                          label: t(
                            "oeuvresDansBibliographieDescriptionReproductionCouleur",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieNotes",
                          label: t("oeuvresDansBibliographieNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "tirageInBibliography",
                      label: t("entrytirages"),
                      type: "link-complex",
                      hidden: !isModulePhotoEnabled,
                      fields: [
                        {
                          name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                          label: t(
                            "bibliographieDescriptionNumeroBibliographie",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "bibliography.bibliographieDescriptionResumeLong",
                          label: t("bibliographieDescriptionResumeLong"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "tirageDansBibliographieDescriptionReproduitPage",
                          label: t(
                            "tirageDansBibliographieDescriptionReproduitPage",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansBibliographieDescriptionCitePage",
                          label: t(
                            "tirageDansBibliographieDescriptionCitePage",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansBibliographieDescriptionPhotographe",
                          label: t(
                            "tirageDansBibliographieDescriptionPhotographe",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansBibliographieDescriptionNonPagine",
                          label: t(
                            "tirageDansBibliographieDescriptionNonPagine",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansBibliographieDescriptionReproductionCouleur",
                          label: t(
                            "tirageDansBibliographieDescriptionReproductionCouleur",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansBibliographieNotes",
                          label: t("tirageDansBibliographieNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedExhibition",
                      label: t("entryexhibitions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAuction",
                      label: t("entryauctions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAuthor",
                      label: t("entryauthors"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "documentInBibliography",
                      label: t("entrydocuments"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "documentsDansBibliographieDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionCitePage",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionCitePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionPhotographe",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionPhotographe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionReproductionCouleur",
                          type: "checkbox",
                          label: t(
                            "documentsDansBibliographieDescriptionReproductionCouleur",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionNonPagine",
                          type: "checkbox",
                          label: t(
                            "documentsDansBibliographieDescriptionNonPagine",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                    {
                      name: "photographyInBibliography",
                      label: t("entryphotographies"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "photographiesDansBibliographieDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "photographiesDansBibliographieDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionCitePage",
                          type: "text",
                          label: t(
                            "photographiesDansBibliographieDescriptionCitePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionPhotographe",
                          type: "text",
                          label: t(
                            "photographiesDansBibliographieDescriptionPhotographe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionReproductionCouleur",
                          type: "checkbox",
                          label: t(
                            "photographiesDansBibliographieDescriptionReproductionCouleur",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionNonPagine",
                          type: "checkbox",
                          label: t(
                            "photographiesDansBibliographieDescriptionNonPagine",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                  ],
                },
              ],
            }}
            data={bibliography}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditBibliographyPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetBibliography(id);

  return (
    <>
      {data && <EditBibliography key={data.id} bibliography={data} />}
      {!data && !isLoading && (
        <h2 className="p-4">bibliographieNexistePasLabel</h2>
      )}
    </>
  );
};
